"use client";

import {
  Box,
  Typography,
  Container,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import { styled } from "@mui/system";
import Button from "@/app/atoms/Button";
import CustomTextField from "@/app/atoms/TextField";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Image from "next/image";
import { useAppDispatch, useAppSelector } from "@/app/store/hook";
import { clearUsersError, loginUser } from "@/app/store/slices/authSlice";
import { useRouter, useSearchParams } from "next/navigation";
import { useState, useEffect, Suspense } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Link from "next/link";
import VaultLoader from "@/app/components/Vault-Loader";
import ForgotPasswordModal from "@/app/components/ForgotPassword";
import Loader from "@/app/components/Loader";

/* ------------------- Styled ------------------- */

const PageWrapper = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  width: "100%",
  position: "relative",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",

  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background: `url(/bg.jpg) center/cover no-repeat`,
    opacity: 0.5,
    zIndex: -1,
  },
}));

const CardWrapper = styled(Box)(({ theme }) => ({
  position: "relative",
  maxWidth: 448,
  boxSizing: "border-box",
  margin: "0 auto",
  padding: theme.spacing(2),
  borderRadius: theme.spacing(2),

  // ✅ CROSS-BROWSER GRADIENT BORDER
  border: "1px solid transparent",
  background: `
    linear-gradient(${theme.palette.background.default}, ${theme.palette.background.default}) padding-box,
    linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}) border-box
  `,

  zIndex: 1,

  // ❌ DELETE the entire "&::before" block that was here
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  height: 110,
  margin: "0 auto",
  position: "relative",
}));

const FormContainer = styled(Box)(({ theme }) => ({
  textAlign: "center",
  marginBottom: theme.spacing(3),
}));

const SignUpContainer = styled(Box)(({ theme }) => ({
  textAlign: "center",
  marginTop: theme.spacing(2),
}));

const GradientText = styled(Link)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  cursor: "pointer",
  textDecoration: "none",
}));

type SnackType = "success" | "error" | "warning" | "info";

/* ------------------- Component ------------------- */

const LoginContent = () => {
  const searchParams = useSearchParams();
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { loading, tempUserId, loginError, currentUser } = useAppSelector(
    (state) => state.user,
  );
  const redirectUrl = searchParams.get("redirect") || "/dashboard";

  // ✅ Save redirect param to sessionStorage on mount so it survives
  // navigating to signup page and back, and is available for verify page
  useEffect(() => {
    const redirectParam = searchParams.get("redirect");
    if (redirectParam) {
      sessionStorage.setItem("redirectAfterLogin", redirectParam);
    }
  }, [searchParams]);

  // useEffect(() => {
  //   dispatch(clearUsersError());
  // }, [dispatch]);

  const [showPassword, setShowPassword] = useState(false);
  // const [showVaultLoader, setShowVaultLoader] = useState(false);
  const [openForgotPassword, setOpenForgotPassword] = useState(false);

  const [snack, setSnack] = useState<{
    open: boolean;
    message: string;
    type: SnackType;
  }>({
    open: false,
    message: "",
    type: "error",
  });

  useEffect(() => {
    if (loginError && loginError !== "SILENT") {
      setSnack({
        open: true,
        message: loginError,
        type: "error",
      });
    }
  }, [loginError]);

  // GIF loader after direct login
  // useEffect(() => {
  //   if (currentUser && !tempUserId) {
  //     // OTP not required, show GIF
  //     setShowVaultLoader(true);
  //     sessionStorage.setItem("showGifOnNextLogin", "true");
  //   }
  // }, [currentUser, tempUserId]);

  const handleVaultComplete = () => {
    router.replace("/dashboard");
  };

  useEffect(() => {
    if (tempUserId) {
      // Redirect to OTP page if OTP required
      router.push("/auth/verify");
    }
  }, [tempUserId, router]);

  const formik = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema: Yup.object({
      email: Yup.string().email("Invalid email").required(),
      password: Yup.string().required(),
    }),
    onSubmit: async (values) => {
      const res = await dispatch(loginUser(values));

      if (loginUser.fulfilled.match(res)) {
        if (res.payload?.otpRequired === false) {
          // OTP not required → show GIF for 6 seconds
          // sessionStorage.setItem("showGifOnNextLogin", "true");
          // setShowVaultLoader(true);
          // setTimeout(() => {
          // router.push("/dashboard");
          // window.location.href = "/dashboard";
          window.location.href = redirectUrl;
          // }, 6000);
          // ✅ triggers VaultLoader
        } else if (res.payload?.otpRequired === true) {
          sessionStorage.setItem("redirectAfterLogin", redirectUrl);
          router.push("/auth/verify"); // OTP page
        }
      }
    },
  });

  // if (showVaultLoader) return <VaultLoader onComplete={handleVaultComplete} />;

  return (
    <PageWrapper>
      <Container maxWidth="xl" disableGutters>
        <FormContainer>
          <LogoContainer>
            <Image
              src="/logo.png"
              alt="Logo"
              fill
              style={{ objectFit: "contain" }}
              priority
            />
          </LogoContainer>

          <Typography variant="h1" mt={2}>
            Welcome Back
          </Typography>

          <Typography
            variant="body3"
            mt={1}
            // color="text.secondary"
            color="common.black"
          >
            Access your Mind Vault.
          </Typography>
        </FormContainer>

        <form onSubmit={formik.handleSubmit}>
          <CardWrapper>
            <Box mb={1}>
              <Typography variant="h3" mb={1}>
                Sign In
              </Typography>

              <Typography variant="body3" color="text.secondary">
                Enter your credentials to access your dashboard
              </Typography>
            </Box>

            {/* EMAIL FIELD */}
            <Box>
              <Typography variant="body3" color="common.black">
                Email
              </Typography>

              <CustomTextField
                name="email"
                placeholder="Enter Your Mail"
                fullWidth
                type="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />

              {/* PASSWORD LABEL */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mt={1}
              >
                <Typography variant="body3" color="common.black">
                  Password
                </Typography>

                {/* <GradientText href="#">Forgot Password?</GradientText> */}
                <GradientText
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setOpenForgotPassword(true);
                  }}
                >
                  Forgot Password?
                </GradientText>
              </Box>

              {/* PASSWORD FIELD */}
              <CustomTextField
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                fullWidth
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.password && Boolean(formik.errors.password)
                }
                helperText={formik.touched.password && formik.errors.password}
                // startIcon={<LockOutlinedIcon />}
                // InputProps={{
                //   endAdornment: (
                //     <IconButton
                //       edge="end"
                //       size="small"
                //       onClick={() => setShowPassword(!showPassword)}
                //     >
                //       {showPassword ? <VisibilityOff /> : <Visibility />}
                //     </IconButton>
                //   ),
                // }}
                endIcon={
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                }
              />

              {/* SUBMIT BUTTON */}
              <Button
                variant="contained"
                fullWidth
                curved
                sx={{ mt: 2 }}
                disabled={loading}
                type="submit"
              >
                {loading ? "Signing In..." : "Sign In"}
              </Button>
            </Box>
          </CardWrapper>
        </form>

        <SignUpContainer>
          <Typography variant="body3">
            Don't have an account?{" "}
            <Link
              href={`/auth/signup${redirectUrl !== "/dashboard" ? `?redirect=${encodeURIComponent(redirectUrl)}` : ""}`}
              style={{ textDecoration: "none" }}
            >
              <Typography component="span" variant="body2" color="common.black">
                Sign Up
              </Typography>
            </Link>
          </Typography>
        </SignUpContainer>
      </Container>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => {
          (setSnack({ ...snack, open: false }), dispatch(clearUsersError()));
        }}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => {
            (setSnack({ ...snack, open: false }), dispatch(clearUsersError()));
          }}
          severity={snack.type}
          variant="filled"
        >
          {snack.message}
        </Alert>
      </Snackbar>

      <ForgotPasswordModal
        open={openForgotPassword}
        onClose={() => setOpenForgotPassword(false)}
      />
    </PageWrapper>
  );
};

export default function Login() {
  return (
    <Suspense
      fallback={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
          }}
        >
          <Loader />
        </Box>
      }
    >
      <LoginContent />
    </Suspense>
  );
}

// export default Login;
